import { useState } from "react"
import { useAuth } from "@edemics/contexts/auth-provider"
import { Button } from "@edemics/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@edemics/ui/dropdown-menu"
import { ChevronDown, User, Users, GraduationCap, Award, Briefcase, Loader2 } from "lucide-react"
import { toast } from "sonner"

/**
 * Tenant multi-app-role switcher.
 *
 * Different from the existing PortalSwitcher (super-admin only, tenant
 * impersonation UI) — this is for tenant users who hold more than one
 * app-role kind (admin + parent, teacher + parent, etc.) and want to
 * flip between them. Reads user.appRoles from AuthProvider.
 *
 * Renders nothing when:
 *   - user is not authenticated
 *   - user is a platform user (they use PortalSwitcher, not this)
 *   - user.appRoles is missing or has <=1 entry (nothing to switch to)
 *
 * On click, calls AuthProvider.switchRole which posts to
 * /auth/switch-role, re-mints the JWT, and navigates to /dashboard.
 */

interface RoleMeta {
  label: string
  icon: JSX.Element
  colorClasses: string
}

const ROLE_META: Record<string, RoleMeta> = {
  admin: {
    label: "Admin Portal",
    icon: <User className="h-4 w-4" />,
    colorClasses: "bg-blue-100 text-blue-700 border-blue-200",
  },
  teacher: {
    label: "Teacher Portal",
    icon: <Briefcase className="h-4 w-4" />,
    colorClasses: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  parent: {
    label: "Parent Portal",
    icon: <Users className="h-4 w-4" />,
    colorClasses: "bg-purple-100 text-purple-700 border-purple-200",
  },
  student: {
    label: "Student Portal",
    icon: <GraduationCap className="h-4 w-4" />,
    colorClasses: "bg-orange-100 text-orange-700 border-orange-200",
  },
  alumni: {
    label: "Alumni Portal",
    icon: <Award className="h-4 w-4" />,
    colorClasses: "bg-amber-100 text-amber-700 border-amber-200",
  },
}

function metaFor(role: string): RoleMeta {
  return (
    ROLE_META[role] ?? {
      label: `${role.charAt(0).toUpperCase() + role.slice(1)} Portal`,
      icon: <User className="h-4 w-4" />,
      colorClasses: "bg-gray-100 text-gray-700 border-gray-200",
    }
  )
}

export function TenantAppRoleSwitcher() {
  const { user, switchRole } = useAuth()
  const [switching, setSwitching] = useState<string | null>(null)

  if (!user || user.kind === "platform") return null
  const roles = user.appRoles ?? []
  if (roles.length <= 1) return null

  const activeRole = user.role
  const active = metaFor(activeRole)

  const handleSwitch = async (targetRole: string) => {
    if (targetRole === activeRole) return
    setSwitching(targetRole)
    try {
      await switchRole(targetRole)
      toast.success(`Switched to ${metaFor(targetRole).label}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to switch role")
    } finally {
      setSwitching(null)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`w-full justify-between gap-2 ${active.colorClasses}`}
        >
          <span className="flex items-center gap-2">
            {switching ? <Loader2 className="h-4 w-4 animate-spin" /> : active.icon}
            <span className="text-sm font-medium">{active.label}</span>
          </span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Switch portal</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {roles.map((role) => {
          const meta = metaFor(role)
          const isActive = role === activeRole
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleSwitch(role)}
              disabled={isActive || switching !== null}
              className="flex items-center gap-2"
            >
              {meta.icon}
              <span className="flex-1">{meta.label}</span>
              {isActive && (
                <span className="text-xs bg-primary text-white px-2 py-0.5 rounded">
                  Current
                </span>
              )}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
